import ComponentVersion from "../api/model/components/ComponentVersion";

import { AssetContentFormat } from "../api/model/snapshots/Asset";
import Fill from "../api/model/snapshots/layers/Fill";
import Layer from "../api/model/snapshots/layers/Layer";
import Rect from "../api/model/snapshots/layers/Rect";
import ApiSnapshot from "../api/model/snapshots/Snapshot";

import Asset, { AssetContent, AssetPercent, AssetStatState } from "../model/Asset";
import Barrel from "../model/Barrel";
import InspectLayer from "../model/InspectLayer";
import ScreenVersion from "../model/ScreenVersion";
import Snapshot, { SnapshotFlowLink } from "../model/Snapshot";

import { IMG_URL } from "../constants";
import { DENSITY } from "../enums";
import { barrelPrefs } from "../prefs";

import { arrayEqual } from "./array";
import BasicRecord from "./BasicRecord";
import { getImageProperties } from "./image";
import { getDensityScale } from "./legacy";
import { rectContains } from "./rectangle";
import { urlResolve } from "./url";

import { RequiredOnly } from "./UtilityTypes";

const CENT = 100;
const THRESHOLD = 10;
const LAYER_DISTANCE = 40;

function createArtboardLayer(name: string, {
    _id,
    width,
    height,
    backgroundColor: color
}: ApiSnapshot): Layer {
    const fills: Fill[] = color ? [{
        fillType: "color",
        color
    }] : [];

    return {
        _id,
        name,
        opacity: 1,
        borderRadius: 0,
        blendMode: "normal",
        rect: {
            x: 0,
            y: 0,
            width,
            height
        },
        type: "shape",
        fills,
        borders: [],
        shadows: []
    };
}

function getBaseAsset(assetVariants: AssetContent[]): AssetContent | undefined {
    return assetVariants.find(({ density, densityScale = getDensityScale(density!), format }) =>
        densityScale === DENSITY["BASE"] && format === AssetContentFormat.Png
    );
}

function getLocalAssetName(barrelId: string, containerId: string, snapshot: Snapshot | ApiSnapshot, assetId: string):
    Promise<string | void> {
    if (!snapshot.assets) {
        return Promise.resolve();
    }

    const asset = (snapshot.assets as Asset[]).find(a => a._id === assetId);

    if (!asset) {
        return Promise.resolve();
    } else if ("localName" in asset) {
        return Promise.resolve((asset as Asset).localName);
    }

    return (barrelPrefs.get(barrelId, containerId, "assetNames") as Promise<BasicRecord>)
        .then(assetNames => assetNames?.[assetId]);
}

function getContentLengthOfAssets(assetContent: RequiredOnly<AssetContent, "url">) {
    const { url, format, density, densityScale = getDensityScale(density!) } = assetContent;

    return fetch(url, { method: "HEAD" }).then(response => ({
        length: response.headers.get("Content-Length") as unknown as number,
        densityScale,
        format
    }));
}

function calculateSVGOptimizationStats(assetVariant: AssetContent): Promise<void> {
    if ("optimized" in assetVariant) {
        return Promise.resolve();
    }

    assetVariant.optimized = {
        ...assetVariant,
        url: `${IMG_URL}/svgo/${encodeURIComponent(assetVariant.url)}`
    };

    return Promise.all([
        assetVariant,
        assetVariant.optimized as RequiredOnly<AssetContent, "url">
    ].map(getContentLengthOfAssets))
        .then(([{ length: length0 }, { length: length1 }]) => {
            assetVariant.optimized!.percent = Math.round((length0 - length1) / length0 * CENT);
        }).catch(() => {
            assetVariant.optimized = null;
        });
}

function calculateOptimizedAssetStats(assetVariant: AssetContent): Promise<void> {
    return Promise.all([
        assetVariant,
        assetVariant.optimized as RequiredOnly<AssetContent, "url">
    ].map(getContentLengthOfAssets))
        .then(([{ length: length0 }, { length: length1 }]) => {
            assetVariant.optimized!.percent = Math.round((length0 - length1) / length0 * CENT);
        }).catch(() => {
            // No-op
        });
}

function calculateAndAddAssetStats(asset: Asset, assetDensityScales: Set<number>): Promise<void> {
    if ("percent" in asset) {
        return Promise.resolve();
    }

    let percent: AssetPercent | undefined;
    let jpgPercent = 0;
    let pngPercent = 0;

    return Promise.all(asset.contents.map(getContentLengthOfAssets)).then(contentLengthList => {
        assetDensityScales.forEach(assetDensityScale => {
            const densityLengthMap = contentLengthList.filter(contentLength =>
                contentLength.densityScale === assetDensityScale
            ).reduce((map, obj) => {
                map[obj.format] = obj.length;
                return map;
            }, {} as Record<AssetContentFormat, number>);

            jpgPercent += Number(densityLengthMap.jpg) / Number(densityLengthMap.png);
            pngPercent += Number(densityLengthMap.png) / Number(densityLengthMap.jpg);
        });
        jpgPercent = Math.round(CENT - jpgPercent / assetDensityScales.size * CENT);
        pngPercent = Math.round(CENT - pngPercent / assetDensityScales.size * CENT);

        if (jpgPercent >= THRESHOLD) {
            percent = {
                type: AssetContentFormat.Jpg,
                diff: jpgPercent
            };
        } else if (pngPercent >= THRESHOLD) {
            percent = {
                type: AssetContentFormat.Png,
                diff: pngPercent
            };
        }

        if (percent) {
            asset.percent = percent;
        }
    }).catch(() => {
        // Fail silently
    });
}

function sortLayersByArea(layers: InspectLayer[]): InspectLayer[] {
    return layers.sort((l1, l2) => l1.rect.width * l1.rect.height - l2.rect.width * l2.rect.height);
}

function findLayersLargerThanRect(
    layerList: InspectLayer[],
    boundingRect: { left: number; top: number; right: number; bottom: number; }
): InspectLayer[] {
    let parentLayers: InspectLayer[] = [];

    for (let i = 0; i < layerList.length; i++) {
        const layer = layerList[i];
        const layerRight = layer.absoluteRect.x + layer.rect.width;
        const layerBottom = layer.absoluteRect.y + layer.rect.height;

        if (
            layer.absoluteRect.x <= boundingRect.left &&
            layer.absoluteRect.y <= boundingRect.top &&
            layerRight >= boundingRect.right &&
            layerBottom >= boundingRect.bottom
        ) {
            if (layer.type !== "group" || ["bottom", "top"].includes(layer.interactionLevel!)) {
                parentLayers.push(layer);
            }

            if (layer.type === "group") {
                parentLayers = parentLayers.concat(findLayersLargerThanRect(layer.layers!, boundingRect));
            }
        }
    }

    return parentLayers;
}

export function findLayersThatContainsPosition({
    layerList, x, y, level = 0, diffLayerSourceIds, includeEmptyGroupLayers = false
} : {
    layerList: InspectLayer[];
    x: number;
    y: number;
    level?: number;
    diffLayerSourceIds?: Set<string> | null;
    includeEmptyGroupLayers?: boolean;
}): InspectLayer[] {
    const MAX_SIZE_TO_ENABLE_HIT_REGION = 3;
    const HIT_REGION_OFFSET = 6;
    let layers: InspectLayer[] = [];

    for (let i = layerList.length - 1; i >= 0; i--) {
        const layer = { ...layerList[i], level };
        const { width, height } = layer.renderRect || layer.rect;

        const horizontalOffset = width < MAX_SIZE_TO_ENABLE_HIT_REGION ? HIT_REGION_OFFSET : 0;
        const verticalOffset = height < MAX_SIZE_TO_ENABLE_HIT_REGION ? HIT_REGION_OFFSET : 0;

        const absoluteRect = layer.absoluteRenderRect || layer.absoluteRect;

        const layerTop = absoluteRect.y - verticalOffset;
        const layerRight = absoluteRect.x + width + horizontalOffset;
        const layerBottom = absoluteRect.y + height + verticalOffset;
        const layerLeft = absoluteRect.x - horizontalOffset;

        if (
            layerLeft <= x &&
            layerTop <= y &&
            layerRight >= x &&
            layerBottom >= y
        ) {
            if (layer.type === "group") {
                if (layer.interactionLevel === "top") {
                    layers.push(layer);
                }

                layers = layers.concat(findLayersThatContainsPosition({
                    layerList: layer.layers!, x, y, level: level + 1, diffLayerSourceIds, includeEmptyGroupLayers
                }));
                if (layer.interactionLevel === "bottom") {
                    layers.push(layer);
                }

                if (includeEmptyGroupLayers && !layer.interactionLevel) {
                    layers.push(layer);
                }
            } else {
                layers.push(layer);
            }
        }
    }

    const foundLayers = layers.reduce((acc, layer) => {
        if (diffLayerSourceIds && layer.sourceId && diffLayerSourceIds.has(layer.sourceId)) {
            acc.diffLayers.push(layer);
        } else {
            acc.layers.push(layer);
        }

        return acc;
    }, { diffLayers: [] as InspectLayer[], layers: [] as InspectLayer[] });

    return [...foundLayers.diffLayers, ...foundLayers.layers];
}

export function areRectsIdentical(rect1: Rect, rect2: Rect): boolean {
    return rect1.x === rect2.x && rect1.y === rect2.y && rect1.width === rect2.width && rect1.height === rect2.height;
}

export function findParentComponentLayers(layer: InspectLayer | null) {
    if (!layer) {
        return null;
    }

    const result = [];
    let currentLayer = layer;
    while (currentLayer.parent) {
        if (currentLayer.parent.componentId) {
            result.push(currentLayer.parent);
        }

        currentLayer = currentLayer.parent;
    }

    return result;
}

export function findCommonParents(layer1: InspectLayer, layer2: InspectLayer, snapshot: Snapshot): InspectLayer[] {
    if (!layer2) {
        return [layer1];
    }

    const boundingRect = {
        left: Math.min(layer1.absoluteRect.x, layer2.absoluteRect.x),
        top: Math.min(layer1.absoluteRect.y, layer2.absoluteRect.y),
        right: Math.max(layer1.absoluteRect.x + layer1.rect.width, layer2.absoluteRect.x + layer2.rect.width),
        bottom: Math.max(layer1.absoluteRect.y + layer1.rect.height, layer2.absoluteRect.y + layer2.rect.height)
    };

    let parentLayers = findLayersLargerThanRect(snapshot.layers, boundingRect);

    if (layer1 === layer2) {
        parentLayers = parentLayers.filter(layer => !areRectsIdentical(layer.absoluteRect, layer1.absoluteRect));
    }

    if (!parentLayers.length) {
        return [snapshot.layers[0]];
    }

    return sortLayersByArea(parentLayers);
}

/**
 * Converts given asset to AssetsStoreTypes Asset
 */
export function addAssetStats(
    barrelId: string,
    containerId: string,
    snapshot: Snapshot | ApiSnapshot,
    asset: Asset
): Promise<unknown[]> {
    const assetExports = {} as Record<AssetContentFormat, number>;
    const assetDensityScales = new Set<number>();

    const optimizedAssets = asset.contents.filter(({ originalURL, format }) =>
        Boolean(originalURL) && format !== AssetContentFormat.Webp && format !== AssetContentFormat.Avif);

    let webpOrAvifAssets = asset.contents.filter(({ format }) =>
        format === AssetContentFormat.Webp || format === AssetContentFormat.Avif);

    // Filter out optimized assets
    asset.contents = asset.contents.filter(({ originalURL }) => !originalURL);

    webpOrAvifAssets = webpOrAvifAssets.map(item => {
        const originalAsset = asset.contents.find(({ url }) => url === item.originalURL)!;

        if (!originalAsset) {
            return item;
        }

        const optimizedContent = item.optimized && typeof item.optimized === "object" ? item.optimized : item;

        return {
            ...originalAsset,
            _id: item._id,
            format: item.format,
            optimized: { ...optimizedContent }
        };
    });

    asset.contents = [...asset.contents, ...webpOrAvifAssets.filter(
        item => !asset.contents.some(content => content._id === item._id)
    )];

    const optimizedAssetStatsPromises: Promise<void>[] = [];

    for (let i = 0; i < asset.contents.length; i++) {
        const {
            url,
            format,
            density,
            densityScale = getDensityScale(density!),
            optimized
        } = asset.contents[i];

        if (!optimized) {
            const optimizedAsset = optimizedAssets.find(({ originalURL }) => originalURL === url);

            if (optimizedAsset) {
                asset.contents[i].optimized = { ...optimizedAsset };
            }
        }

        const optimizedVariant = asset.contents[i].optimized;

        if (optimizedVariant && optimizedVariant.url && optimizedVariant.status === "ready") {
            optimizedAssetStatsPromises.push(calculateOptimizedAssetStats(asset.contents[i]));
        }

        if (format in assetExports) {
            assetExports[format]++;
        } else {
            assetExports[format] = 1;
        }

        assetDensityScales.add(densityScale);
    }

    let statPromise: Promise<void>;
    if (
        AssetContentFormat.Jpg in assetExports &&
        !(AssetContentFormat.Svg in assetExports) &&
        !(AssetContentFormat.Pdf in assetExports) &&
        assetExports.jpg === assetExports.png
    ) {
        statPromise = calculateAndAddAssetStats(asset, assetDensityScales);
    } else if (AssetContentFormat.Svg in assetExports) {
        statPromise = calculateSVGOptimizationStats(
            asset.contents.find(({ format }) => format === AssetContentFormat.Svg)!
        );
    } else {
        statPromise = Promise.resolve();
    }

    const baseAsset = getBaseAsset(asset.contents);
    const imgPropPromise = !baseAsset || "lightness" in asset
        ? Promise.resolve()
        : getImageProperties(baseAsset.url).then(props => Object.assign(asset, props)).catch(() => null);

    const assetNamePromise = "localName" in asset
        ? Promise.resolve()
        : getLocalAssetName(barrelId, containerId, snapshot, asset._id).then(localName => {
            if (localName) {
                Object.assign(asset, { localName });
            }
        });

    return Promise.all([statPromise, imgPropPromise, assetNamePromise, ...optimizedAssetStatsPromises]);
}

/**
 * Converts given layers to {@link InspectLayer}
 */
export function preprocessLayers(layers: Layer[], parent: InspectLayer | null = null) {
    const { absoluteRect: parentAbsoluteRect = { x: 0, y: 0 } } = parent ?? {};

    for (const layer of layers) {
        (layer as InspectLayer).absoluteRect = {
            x: layer.rect.x + parentAbsoluteRect.x,
            y: layer.rect.y + parentAbsoluteRect.y,
            width: layer.rect.width,
            height: layer.rect.height
        };

        if (layer.renderRect) {
            (layer as InspectLayer).absoluteRenderRect = {
                x: layer.renderRect.x + parentAbsoluteRect.x,
                y: layer.renderRect.y + parentAbsoluteRect.y,
                width: layer.renderRect.width,
                height: layer.renderRect.height
            };
        }

        (layer as InspectLayer).parent = parent;

        if (layer.type === "group") {
            if (Array.isArray(layer.layers)) {
                preprocessLayers(layer.layers, layer as InspectLayer);
            } else {
                layer.layers = [];
            }
        }
    }
}

export interface LayerClickHandler {
    reset: () => void;
    getLayerToSelectFromClick: (args: {
        ev: MouseEvent;
        layerData: InspectLayer;
        snapshot: Snapshot;
        selectedLayer: InspectLayer;
        snapshotDetailRect: Pick<DOMRect, "left" | "top" | "width" | "height">;
        diffLayerSourceIds?: Set<string> | null | undefined;
    }) => InspectLayer;
}

export function getLayerClickHandler(): LayerClickHandler {
    let lastSelectedLayerX: number | null = null;
    let lastSelectedLayerY: number | null = null;

    return {
        reset() {
            lastSelectedLayerX = null;
            lastSelectedLayerY = null;
        },
        getLayerToSelectFromClick({
            ev,
            layerData,
            snapshot: { layers, width, height },
            selectedLayer,
            snapshotDetailRect,
            diffLayerSourceIds
        }) {
            let layerToSelect = layerData;
            const x = (ev.clientX - snapshotDetailRect.left) / snapshotDetailRect.width * width;
            const y = (ev.clientY - snapshotDetailRect.top) / snapshotDetailRect.height * height;

            const layersThatContainsPosition = findLayersThatContainsPosition({
                layerList: layers, x, y, diffLayerSourceIds
            });

            if (lastSelectedLayerX && lastSelectedLayerY && selectedLayer) {
                const lastLayersThatContainsPosition = findLayersThatContainsPosition({
                    layerList: layers, x: lastSelectedLayerX, y: lastSelectedLayerY, diffLayerSourceIds
                });

                if (Math.hypot(lastSelectedLayerX - x, lastSelectedLayerY - y) > LAYER_DISTANCE ||
                    !arrayEqual(
                        layersThatContainsPosition,
                        lastLayersThatContainsPosition,
                        (l1, l2) => l1._id === l2._id
                    )) {
                    [layerToSelect] = layersThatContainsPosition;
                } else {
                    const selectedLayerId = selectedLayer._id;
                    const selectedLayerIndex = layersThatContainsPosition.findIndex(
                        layer => layer._id === selectedLayerId
                    );
                    layerToSelect = layersThatContainsPosition[
                        (selectedLayerIndex + 1) % layersThatContainsPosition.length
                    ];
                }
            } else {
                [layerToSelect] = layersThatContainsPosition;
            }

            lastSelectedLayerX = x;
            lastSelectedLayerY = y;
            return layerToSelect;
        }
    };
}

export function findCoveringLink(
    barrel: Barrel,
    version: ScreenVersion | ComponentVersion,
    layer: InspectLayer | null
): SnapshotFlowLink | null {
    if (!layer || !version.snapshot.links) {
        return null;
    }

    const coveringLink = version.snapshot.links.find(link => rectContains(link.rect, layer.absoluteRect));
    if (!coveringLink) {
        return null;
    }

    let destinationScreen;
    let name: string;
    let url: string | undefined;

    switch (coveringLink.destination.type) {
        case "previous":
            name = "previous screen";
            break;

        case "screen":
            name = coveringLink.destination.name!;
            destinationScreen = barrel.findScreenByName(coveringLink.destination.name!);

            if (destinationScreen) {
                url = urlResolve("project-screen", { url: { pid: barrel.id, sid: destinationScreen._id } });
            }

            break;

        default:
            return null;
    }

    return { name, url };
}

/**
 * Extends snapshot's asset model (single asset if assetId given, or whole assets otherwise) with given AssetState
 * field.
 */
export function extendSnapshotWithGivenAssetState<T extends ApiSnapshot>(
    snapshot: Partial<T>,
    statState: AssetStatState,
    assetId?: string
): Partial<T> {
    return {
        ...snapshot,
        assets: assetId
            ? snapshot.assets?.map(asset => {
                if (assetId === asset._id) {
                    return { ...asset, statState };
                }

                return asset;
            })
            : snapshot.assets?.map(asset => ({ ...asset, statState }))
    };
}

export function addArtboardLayerIfNecessary(snapshot: ApiSnapshot, screenName: string): ApiSnapshot {
    const currentLayers = snapshot.layers;
    const artboardLevelLayerAlreadyExist = currentLayers.some(({ name, rect: { width, height } }) => (
        name === screenName &&
        width === snapshot.width &&
        height === snapshot.height
    ));

    if (artboardLevelLayerAlreadyExist) {
        return snapshot;
    }

    const artboardLayer = createArtboardLayer(screenName, snapshot);
    snapshot.layers.unshift(artboardLayer);

    return snapshot;
}
